import { BatchItems } from './chunk-KUFM6UR4.js';
import { identifierText, IdentifierFact } from './chunk-OAF4WNMT.js';
import { ProtocolIcon } from './chunk-7K5EYODF.js';
import { CHAINLINK_CCIP, CCIP_COMPONENTS, CCIP_CURSE_SUBJECT_KIND, CCIP_RECEIPT_ROLE_KIND, CCIP_VERIFICATION_KIND, CCIP_TOKEN_TRANSFERS_KIND, CCIP_FIELD_LABELS, CCIP_MESSAGE_ID_FACT, ProtocolValue, hasBytesPayload, FactGrid, ProtocolBadge, SectionCard, ProtocolParamList, fieldAtPath, CCIP_RECEIPT_ROLE_LABELS, legField, decodedOrRaw } from './chunk-XH4WXZPE.js';
import { ShieldAlert, Network, Waypoints, CircleHelp, Coins, Cog, BadgeCheck, Send, ArrowLeftRight, ArrowRight, ShieldCheck, Users } from 'lucide-react';
import { RawToggle } from '@authe/ui-core';
import { text } from '@authe/fe-commons/display';
import { displayOf } from '@authe/fe-commons/storage';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import * as React4 from 'react';
import { isValidAddress } from '@authe/fe-commons/chains';

function isRecord(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function CcipCurseSubject({ display, raw, decoded }) {
  const d = isRecord(decoded) ? decoded : {};
  const chainName = text(display) || text(displayOf(d.sourceChain)) || text(displayOf(d.sourceChainId));
  const selector = text(displayOf(d.sourceChainSelector));
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5", children: [
    /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 rounded-sm bg-risk-high/10 px-1.5 py-px text-2xs font-medium text-risk-high", children: [
      /* @__PURE__ */ jsx(ShieldAlert, { className: "size-3", "aria-hidden": true }),
      "RMN curse"
    ] }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: chainName || "unknown chain" }),
    selector && /* @__PURE__ */ jsxs("span", { className: "font-mono text-xs text-muted-foreground", children: [
      "selector ",
      selector
    ] }),
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
}
function ccipMessageField(message, ...names) {
  return legField(message, ...names) ?? legField(legField(message, "header"), ...names);
}
function ccipMessageFacts(get) {
  const messageId = get("messageId", "message_id");
  const sourceChain = get("sourceChainSelector", "sourceChain");
  const destChain = get("destChainSelector", "destinationChainSelector", "destChain");
  const sender = get("sender", "originalSender");
  const receiver = get("receiver", "messageReceiver", "recipient");
  const feeToken = get("feeToken");
  const feeAmount = get("feeTokenAmount");
  const gasLimit = get("gasLimit");
  const sequence = get("sequenceNumber", "sequence");
  const nonce = get("nonce");
  const tokenAmounts = get("tokenAmounts");
  const data = get("data");
  const verification = get("verification");
  return [
    {
      label: CCIP_FIELD_LABELS.messageId,
      // CCIP's own fact: linked to the CCIP explorer by the definition's
      // `fact_links`, through the one identifier renderer.
      value: /* @__PURE__ */ jsx(IdentifierFact, { protocolId: CHAINLINK_CCIP.protocolId, fact: CCIP_MESSAGE_ID_FACT, value: messageId }),
      when: identifierText(messageId)
    },
    { label: CCIP_FIELD_LABELS.sourceChainSelector, value: /* @__PURE__ */ jsx(ProtocolValue, { value: sourceChain }), when: sourceChain != null },
    { label: CCIP_FIELD_LABELS.destChainSelector, value: /* @__PURE__ */ jsx(ProtocolValue, { value: destChain }), when: destChain != null },
    { label: CCIP_FIELD_LABELS.sender, value: /* @__PURE__ */ jsx(ProtocolValue, { value: sender }), when: sender },
    { label: CCIP_FIELD_LABELS.receiver, value: /* @__PURE__ */ jsx(ProtocolValue, { value: receiver }), when: receiver },
    {
      label: CCIP_FIELD_LABELS.feeTokenAmount,
      value: /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap items-center gap-1.5", children: [
        feeAmount != null && /* @__PURE__ */ jsx(ProtocolValue, { value: feeAmount }),
        feeToken != null && /* @__PURE__ */ jsx(ProtocolValue, { value: feeToken })
      ] }),
      when: feeAmount != null || feeToken != null
    },
    { label: CCIP_FIELD_LABELS.gasLimit, value: /* @__PURE__ */ jsx(ProtocolValue, { value: gasLimit }), when: gasLimit != null },
    { label: CCIP_FIELD_LABELS.sequenceNumber, value: /* @__PURE__ */ jsx(ProtocolValue, { value: sequence }), when: sequence != null },
    { label: CCIP_FIELD_LABELS.nonce, value: /* @__PURE__ */ jsx(ProtocolValue, { value: nonce }), when: nonce != null },
    {
      label: CCIP_FIELD_LABELS.tokenAmounts,
      value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenAmounts }),
      // A receive message's `tokenAmounts` is often `[]`; skip the empty row.
      when: Array.isArray(tokenAmounts) ? tokenAmounts.length > 0 : tokenAmounts
    },
    { label: CCIP_FIELD_LABELS.data, value: /* @__PURE__ */ jsx(ProtocolValue, { value: data }), when: hasBytesPayload(data) },
    { label: CCIP_FIELD_LABELS.verification, value: /* @__PURE__ */ jsx(ProtocolValue, { value: verification }), when: verification }
  ];
}
function CcipMessage({ message }) {
  const facts = React4.useMemo(
    () => ccipMessageFacts((...names) => ccipMessageField(message, ...names)),
    [message]
  );
  return /* @__PURE__ */ jsx(FactGrid, { facts });
}
function asList(value) {
  return Array.isArray(value) ? value : value != null ? [value] : [];
}
function asMessages(value) {
  const decoded = decodedOrRaw(value);
  return Array.isArray(decoded) ? decoded : [];
}
function reportMessages(call) {
  const report = fieldAtPath(call.params, "report", "rawReport", "encodedReport", "executionReport");
  const reports = asList(decodedOrRaw(report));
  return reports.flatMap((r) => asMessages(legField(r, "messages")));
}
function CcipExecutionReport({ call, hint }) {
  const messages = React4.useMemo(() => reportMessages(call), [call]);
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: CHAINLINK_CCIP.protocolId, icon: Network }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? call.label ?? "CCIP execute" })
  ] });
  const meta = /* @__PURE__ */ jsxs(Fragment, { children: [
    messages.length > 0 && /* @__PURE__ */ jsxs("span", { className: "text-2xs text-muted-foreground", children: [
      messages.length,
      " ",
      messages.length === 1 ? "message" : "messages"
    ] }),
    /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: CHAINLINK_CCIP.protocolId })
  ] });
  return /* @__PURE__ */ jsxs(SectionCard, { title, protocolId: CHAINLINK_CCIP.protocolId, meta, children: [
    /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs text-muted-foreground", children: "Messages submitted for execution; receipt outcomes establish delivery." }),
    messages.length > 0 ? /* @__PURE__ */ jsx("div", { className: "space-y-2", children: /* @__PURE__ */ jsx(BatchItems, { items: messages, noun: "Message", renderItem: (message) => /* @__PURE__ */ jsx(CcipMessage, { message }) }) }) : (
      // No messages resolved from the report (an unexpected/empty shape):
      // fall back to the generic argument list rather than an empty card.
      /* @__PURE__ */ jsx(ProtocolParamList, { params: call.params, emptyLabel: "No decoded arguments" })
    ),
    /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
      /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call.params, emptyLabel: "No decoded arguments" }) })
    ] })
  ] });
}
function CcipMessageSent({ event }) {
  const facts = React4.useMemo(
    () => ccipMessageFacts((...names) => fieldAtPath(event.params, ...names) ?? ccipMessageField(fieldAtPath(event.params, "message", "encodedMessage"), ...names)),
    [event]
  );
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: CHAINLINK_CCIP.protocolId, icon: Waypoints }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: event.name ?? "CCIP message" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: CHAINLINK_CCIP.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: CHAINLINK_CCIP.protocolId }),
      children: [
        /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs text-muted-foreground", children: "Source message emitted; destination delivery is recorded separately." }),
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All message fields" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: event.params, emptyLabel: "No decoded fields" }) })
        ] })
      ]
    }
  );
}
function isRecord2(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
var PARTICIPANT_STYLE = { icon: CircleHelp, cls: "bg-muted text-muted-foreground" };
var ROLE_STYLE = {
  verifier: { icon: BadgeCheck, cls: "bg-category-emerald/10 text-category-emerald" },
  executor: { icon: Cog, cls: "bg-category-blue/10 text-category-blue" },
  onramp: { icon: Network, cls: "bg-category-violet/10 text-category-violet" },
  token_pool: { icon: Coins, cls: "bg-category-amber/10 text-category-amber" },
  participant: PARTICIPANT_STYLE
};
function CcipReceiptRole({ display, decoded }) {
  const d = isRecord2(decoded) ? decoded : {};
  const role = text(d.role) || "participant";
  const label = text(display) || text(d.label) || CCIP_RECEIPT_ROLE_LABELS[role] || role;
  const style = ROLE_STYLE[role] ?? PARTICIPANT_STYLE;
  const Icon = style.icon;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: `inline-flex items-center gap-1 rounded-sm px-1.5 py-px text-2xs font-medium ${style.cls}`,
      children: [
        /* @__PURE__ */ jsx(Icon, { className: "size-3", "aria-hidden": true }),
        label
      ]
    }
  );
}
function CcipSend({ call }) {
  const message = fieldAtPath(call.params, "message");
  const extraArgs = legField(message, "extraArgs");
  const destChain = fieldAtPath(call.params, "destinationChainSelector", "destChainSelector", "destChain");
  const receiver = legField(message, "receiver", "recipient");
  const feeToken = legField(message, "feeToken");
  const tokenAmounts = legField(message, "tokenAmounts");
  const data = legField(message, "data");
  const gasLimit = legField(extraArgs, "gasLimit");
  const allowOoo = legField(extraArgs, "allowOutOfOrderExecution");
  const messageId = call.returnParams?.[0]?.value;
  const facts = [
    {
      label: CCIP_FIELD_LABELS.contract,
      value: call.address != null ? /* @__PURE__ */ jsx(ProtocolValue, { value: call.address }) : void 0,
      when: call.address
    },
    { label: CCIP_FIELD_LABELS.destChainSelector, value: /* @__PURE__ */ jsx(ProtocolValue, { value: destChain }), when: destChain != null },
    { label: CCIP_FIELD_LABELS.receiver, value: /* @__PURE__ */ jsx(ProtocolValue, { value: receiver }), when: receiver },
    {
      label: CCIP_FIELD_LABELS.tokenAmounts,
      value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenAmounts }),
      when: Array.isArray(tokenAmounts) ? tokenAmounts.length > 0 : tokenAmounts
    },
    { label: CCIP_FIELD_LABELS.feeToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: feeToken }), when: feeToken },
    { label: CCIP_FIELD_LABELS.data, value: /* @__PURE__ */ jsx(ProtocolValue, { value: data }), when: hasBytesPayload(data) },
    { label: CCIP_FIELD_LABELS.gasLimit, value: /* @__PURE__ */ jsx(ProtocolValue, { value: gasLimit }), when: gasLimit != null },
    {
      label: CCIP_FIELD_LABELS.allowOutOfOrderExecution,
      value: /* @__PURE__ */ jsx(ProtocolValue, { value: allowOoo }),
      when: allowOoo != null
    },
    {
      label: CCIP_FIELD_LABELS.messageId,
      value: /* @__PURE__ */ jsx(IdentifierFact, { protocolId: CHAINLINK_CCIP.protocolId, fact: CCIP_MESSAGE_ID_FACT, value: messageId }),
      when: identifierText(messageId)
    }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: CHAINLINK_CCIP.protocolId, icon: Send }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: call.label ?? "ccipSend" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: CHAINLINK_CCIP.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: CHAINLINK_CCIP.protocolId }),
      children: [
        /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs text-muted-foreground", children: "Send request; destination execution is recorded separately." }),
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}
function isRecord3(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function asLeaf(value) {
  const inner = displayOf(value);
  if (inner === null || inner === void 0 || inner === "") return void 0;
  return typeof inner === "string" ? inner : String(inner);
}
function legsOf(value) {
  const items = Array.isArray(value) ? value : isRecord3(value) ? [value] : [];
  return items.map((item) => {
    if (!isRecord3(item)) return null;
    const token = asLeaf(item.token);
    const amount = asLeaf(item.amount);
    const sourcePool = asLeaf(item.sourcePoolAddress);
    const destToken = asLeaf(item.destTokenAddress);
    if (token === void 0 && amount === void 0 && sourcePool === void 0 && destToken === void 0)
      return null;
    return { token, amount, sourcePool, destToken };
  }).filter((leg) => leg !== null);
}
function CcipTokenAmounts({
  decoded,
  chain,
  sourceChain,
  destinationChain,
  renderAddress
}) {
  const legs = legsOf(decoded);
  if (legs.length === 0) return null;
  const addr = (value, addressChain) => addressChain && isValidAddress(value, addressChain) && renderAddress ? renderAddress(value, { chain: addressChain }) : /* @__PURE__ */ jsx("span", { className: "break-all font-mono text-xs", title: value, children: value });
  return /* @__PURE__ */ jsx("span", { className: "inline-flex min-w-0 flex-col gap-1.5", children: legs.map((leg, i) => /* @__PURE__ */ jsxs(
    "span",
    {
      className: "inline-flex min-w-0 flex-col gap-0.5",
      children: [
        /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(ArrowLeftRight, { className: "size-3 shrink-0 text-muted-foreground", "aria-hidden": true }),
          leg.amount != null && /* @__PURE__ */ jsxs("span", { className: "font-mono text-sm tabular-nums", title: leg.amount, children: [
            leg.amount,
            /* @__PURE__ */ jsx("span", { className: "ml-1 text-2xs text-muted-foreground", children: "base units" })
          ] }),
          leg.token != null && addr(leg.token, chain)
        ] }),
        (leg.sourcePool != null || leg.destToken != null) && /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap items-center gap-1 pl-4 text-xs text-muted-foreground", children: [
          leg.sourcePool != null && addr(leg.sourcePool, sourceChain),
          /* @__PURE__ */ jsx(ArrowRight, { className: "size-3 shrink-0", "aria-hidden": true }),
          leg.destToken != null && addr(leg.destToken, destinationChain)
        ] })
      ]
    },
    `${leg.token ?? "t"}:${leg.amount ?? i}`
  )) });
}
function isRecord4(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function addrList(v) {
  return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
}
function Row({
  icon: Icon,
  label,
  children
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [
    /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 text-2xs font-medium text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Icon, { className: "size-3", "aria-hidden": true }),
      label
    ] }),
    children
  ] });
}
function CcipVerification({
  decoded,
  chain,
  renderAddress
}) {
  const d = isRecord4(decoded) ? decoded : {};
  const verifiers = addrList(d.verifiers);
  const executor = typeof d.executor === "string" ? d.executor : "";
  const participants = addrList(d.participants);
  const blobs = typeof d.verifier_blob_count === "number" ? d.verifier_blob_count : typeof d.attestation_count === "number" ? d.attestation_count : void 0;
  const addr = (a) => renderAddress ? renderAddress(a, { chain }) : /* @__PURE__ */ jsx("span", { className: "font-mono text-xs", children: a });
  return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1 rounded-sm border border-border/60 bg-muted/30 px-2 py-1.5", children: [
    /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 text-2xs font-semibold text-muted-foreground", children: [
      /* @__PURE__ */ jsx(ShieldCheck, { className: "size-3.5", "aria-hidden": true }),
      "Source verification configuration",
      blobs != null && /* @__PURE__ */ jsxs("span", { className: "font-normal text-muted-foreground", children: [
        "\xB7 ",
        blobs,
        " verifier blob",
        blobs === 1 ? "" : "s"
      ] })
    ] }),
    verifiers.length > 0 && /* @__PURE__ */ jsx(Row, { icon: BadgeCheck, label: verifiers.length > 1 ? "Verifiers" : "Verifier", children: verifiers.map((v) => /* @__PURE__ */ jsx(React4.Fragment, { children: addr(v) }, v)) }),
    executor && /* @__PURE__ */ jsx(Row, { icon: Cog, label: "Executor", children: addr(executor) }),
    participants.length > 0 && /* @__PURE__ */ jsx(Row, { icon: Users, label: "Participants", children: participants.map((p) => /* @__PURE__ */ jsx(React4.Fragment, { children: addr(p) }, p)) })
  ] });
}

// src/protocols/chainlink_ccip/index.ts
var PID = CHAINLINK_CCIP.protocolId;
var CHAINLINK_CCIP_HINTS = {
  valueHints: [
    {
      protocolId: PID,
      kind: CCIP_CURSE_SUBJECT_KIND,
      component: CCIP_COMPONENTS.curseSubject,
      fields: []
    },
    {
      protocolId: PID,
      kind: CCIP_RECEIPT_ROLE_KIND,
      component: CCIP_COMPONENTS.receiptRole,
      fields: ["role", "label"]
    },
    {
      protocolId: PID,
      kind: CCIP_VERIFICATION_KIND,
      component: CCIP_COMPONENTS.verification,
      fields: ["verifiers", "executor", "participants", "attestation_count"]
    },
    {
      // The `token_amounts` highlight (the `[{token, amount, sourcePoolAddress,
      // destTokenAddress}, …]` transfer list). Scoped to CCIP so the generic
      // `token_transfers` kind resolves to the CCIP renderer only on a CCIP
      // highlight.
      protocolId: PID,
      kind: CCIP_TOKEN_TRANSFERS_KIND,
      component: CCIP_COMPONENTS.tokenAmounts,
      fields: ["token", "amount", "sourcePoolAddress", "destTokenAddress"]
    }
  ],
  eventHints: [
    { protocolId: PID, event: "CCIPSendRequested", component: CCIP_COMPONENTS.messageSent, summaryFields: [] },
    { protocolId: PID, event: "CCIPMessageSent", component: CCIP_COMPONENTS.messageSent, summaryFields: [] }
  ],
  callHints: [
    { protocolId: PID, call: "ccipSend", component: CCIP_COMPONENTS.send, summaryFields: [] },
    // The OffRamp receive entrypoints carry the OCR ExecutionReport: `execute`
    // on 1.6, `transmit` on the 1.5 OCR OffRamp. Both resolve to the same
    // message-list card (scoped to CCIP, so an Automation `transmit` on another
    // tx is unaffected). Keep in sync with the backend CCIP `call_views`.
    { protocolId: PID, call: "execute", component: CCIP_COMPONENTS.executionReport, summaryFields: [] },
    { protocolId: PID, call: "transmit", component: CCIP_COMPONENTS.executionReport, summaryFields: [] }
  ]
};
function registerChainlinkCcip(registry) {
  registry.registerValue(CCIP_COMPONENTS.curseSubject, CcipCurseSubject);
  registry.registerValue(CCIP_COMPONENTS.receiptRole, CcipReceiptRole);
  registry.registerValue(CCIP_COMPONENTS.verification, CcipVerification);
  registry.registerValue(CCIP_COMPONENTS.tokenAmounts, CcipTokenAmounts);
  registry.registerEvent(CCIP_COMPONENTS.messageSent, CcipMessageSent);
  registry.registerCall(CCIP_COMPONENTS.send, CcipSend, { ownsReturns: true });
  registry.registerCall(CCIP_COMPONENTS.executionReport, CcipExecutionReport);
  registry.registerHints(CHAINLINK_CCIP_HINTS);
  return registry;
}

export { CHAINLINK_CCIP_HINTS, CcipCurseSubject, CcipExecutionReport, CcipMessage, CcipMessageSent, CcipReceiptRole, CcipSend, CcipTokenAmounts, CcipVerification, ccipMessageFacts, registerChainlinkCcip };
//# sourceMappingURL=chunk-RQVIBGWV.js.map
//# sourceMappingURL=chunk-RQVIBGWV.js.map